import { type Key, type ReactElement, type ReactNode, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { User01 } from "@untitledui/icons";
import { Badge, Select } from "@tricura/atlas-ui";
import { listAllUsers, userLabel } from "../api";
import type { HeaderPatch } from "../pages/submission/layout";
import type { SubmissionForm, SubmissionStatus, SubmissionType } from "../types/submission";
import { formatSubmissionId } from "../utils/submission-id";

const SUBMISSION_TYPE_LABEL: Record<SubmissionType, string> = {
    "New Business": "New Business",
    Renewal: "Renewal",
    Endorsement: "Endorsement",
    Tail: "Tail",
};

type DotColor = "warning" | "success" | "error" | "brand" | "gray";

const dotClass: Record<DotColor, string> = {
    warning: "bg-fg-warning-secondary",
    success: "bg-fg-success-secondary",
    error: "bg-fg-error-secondary",
    brand: "bg-fg-brand-primary",
    gray: "bg-fg-quaternary",
};

const Dot = ({ color }: { color: DotColor }) => <span aria-hidden="true" className={`size-2 shrink-0 rounded-full ${dotClass[color]}`} />;

/** Compute initials from a person's full name. "Dario Erazo" → "DE";
 *  single names use the first two letters. */
function initialsFor(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Tailwind 50/700 colour pairs the InitialsBadge cycles through. The
 *  pair is chosen deterministically from a hash of the user's name so
 *  the same person always lands on the same colour across renders +
 *  dropdown states. Pulled from the standard Tailwind palette so the
 *  contrast ratio of every pair is comparable. */
const INITIALS_COLOURS: { bg: string; text: string }[] = [
    { bg: "#F0F9FF", text: "#0369A1" }, // sky
    { bg: "#FFF1F2", text: "#BE123C" }, // rose
    { bg: "#FFFBEB", text: "#B45309" }, // amber
    { bg: "#ECFDF5", text: "#047857" }, // emerald
    { bg: "#F5F3FF", text: "#6D28D9" }, // violet
    { bg: "#ECFEFF", text: "#0E7490" }, // cyan
    { bg: "#FDF2F8", text: "#BE185D" }, // pink
    { bg: "#FFF7ED", text: "#C2410C" }, // orange
];

/** djb2-flavoured 32-bit hash. Same input → same output, no platform
 *  surprises. Used to pick a stable colour slot per name. */
function colourSlotFor(name: string): { bg: string; text: string } {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
    }
    return INITIALS_COLOURS[hash % INITIALS_COLOURS.length];
}

/** Round badge with the user's initials. Used as the leading icon in
 *  the Underwriter / Approval UW selectors. Colour is derived from the
 *  name so each person gets a stable, distinct chip — easy to scan
 *  visually, no chance of two visible chips reading as the "same
 *  user". Border stays neutral so the chip still feels like a tile,
 *  not a brand pill. */
const InitialsBadge = ({ name }: { name: string }) => {
    const { bg, text } = colourSlotFor(name);
    return (
        <span
            aria-hidden="true"
            data-icon
            className="size-5 inline-flex shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
            style={{
                backgroundColor: bg,
                color: text,
                border: "1px solid #E5E5E5",
            }}
        >
            {initialsFor(name)}
        </span>
    );
};

/** Round neutral user icon. Used as the leading icon in the Approval UW
 *  selector regardless of selection state — the slot is meant to be
 *  visually quiet while the field is the secondary approver. */
const NeutralUserBadge = () => (
    <span
        aria-hidden="true"
        data-icon
        className="size-5 inline-flex shrink-0 items-center justify-center rounded-full"
        style={{
            backgroundColor: "#F5F5F5",
            color: "#A3A3A3",
            border: "1px solid #E5E5E5",
        }}
    >
        <User01 className="size-3 stroke-[2.25px]" />
    </span>
);

const statuses: { id: SubmissionStatus; label: string; icon: ReactElement }[] = [
    { id: "Draft", label: "Draft", icon: <Dot color="gray" /> },
    // `Awaiting BOR` shares the warning dot with `Underwriting` —
    // both communicate an in-flight, "waiting on someone" state.
    { id: "Awaiting BOR", label: "Awaiting BOR", icon: <Dot color="warning" /> },
    { id: "Underwriting", label: "Underwriting", icon: <Dot color="warning" /> },
    { id: "Indications Sent", label: "Indications Sent", icon: <Dot color="warning" /> },
    { id: "Quote Sent", label: "Quote Sent", icon: <Dot color="brand" /> },
    { id: "Bound", label: "Bound", icon: <Dot color="success" /> },
    { id: "Risk Denied", label: "Risk Denied", icon: <Dot color="error" /> },
    { id: "Risk Closed by Agent", label: "Risk Closed by Agent", icon: <Dot color="error" /> },
];

type UnderwriterItem = { id: number; label: string; icon: ReactElement };

function toItem(id: number, label: string): UnderwriterItem {
    return { id, label, icon: <InitialsBadge name={label} /> };
}

/**
 * Underwriter / Approval UW roster: every active user from `/v2/user/`
 * (no role filtering, roles aren't reliably set yet).
 */
function useUnderwriterRoster(): UnderwriterItem[] {
    const [users, setUsers] = useState<UnderwriterItem[]>([]);

    useEffect(() => {
        let cancelled = false;
        void listAllUsers()
            .then((rows) => !cancelled && setUsers(rows.map((u) => toItem(u.id, userLabel(u)))))
            .catch((err) => console.error("Failed to load underwriters", err));
        return () => {
            cancelled = true;
        };
    }, []);

    return users;
}

/** Roster for one selector. Its current assignment is kept as an option
 *  when it isn't in the roster (e.g. a since-deactivated user) so the
 *  trigger still shows it, without offering it to the other selector. */
function withAssigned(roster: UnderwriterItem[], id: number | null): UnderwriterItem[] {
    if (id === null || roster.some((u) => u.id === id)) return roster;
    return [...roster, toItem(id, `User #${id}`)];
}

interface SubmissionHeaderProps {
    detail: SubmissionForm;
    /** Persist a partial change (status/assignee/approver). Layout owns
     *  the call to the backend + optimistic update. */
    onPatch?: (patch: HeaderPatch) => void;
    /** Optional slot rendered between the title block and the dropdown
     *  row. The mobile section navigator (a `<Disclosure>` wrapping
     *  `<SubmissionNav />`) lives here per Figma 356-20885 — caller is
     *  expected to gate its own visibility (e.g. `lg:hidden`). */
    sectionNav?: ReactNode;
}

function keyToNumber(key: Key | null): number | null {
    if (key === null || key === undefined) return null;
    const n = Number(key);
    return Number.isFinite(n) ? n : null;
}

function selectedKeyForId(id: number | null): string | number | null {
    return id;
}

export function SubmissionHeader({ detail, onPatch, sectionNav }: SubmissionHeaderProps) {
    const navigate = useNavigate();
    const roster = useUnderwriterRoster();
    const underwriterItems = useMemo(() => withAssigned(roster, detail.assignee), [roster, detail.assignee]);
    const approverItems = useMemo(() => withAssigned(roster, detail.approver), [roster, detail.approver]);
    const isEdit = !!detail.id;
    const idLabel = isEdit ? formatSubmissionId(detail.id) : "New Submission";
    // The title slot next to the TRI- code reads from Submission
    // Title only — the FNI lives on its own pill in the subtitle.
    const titleName = detail.accountBasics.title;

    // Subtitle text is composed live: brokerage name · type. The
    // FNI lives in its own blue pill badge to the right — it shows
    // the actual fiscal name when one exists, falling back to the
    // literal "FNI" label when the field is still empty (placeholder
    // for the value the document extractor will write later).
    const brokerageName = detail.brokerage.brokerageName?.trim();
    const typeLabel = detail.accountBasics.type ? SUBMISSION_TYPE_LABEL[detail.accountBasics.type] : null;
    const subtitleParts = [brokerageName, typeLabel].filter(Boolean) as string[];
    const fniValue = detail.accountBasics.fni?.trim();
    const fniBadgeLabel = fniValue || (isEdit ? "FNI" : null);

    return (
        <header className="border-secondary bg-primary mt-4 flex flex-col gap-4 border-b px-4 pb-4 pt-0 sm:gap-6 sm:px-8 sm:pb-6 xl:flex-row xl:items-end xl:justify-between">
            {/* Back arrow replaces the old breadcrumb (Andressa jun-26): it
              * took up a whole row, so it's now a compact ← next to the
              * title that returns to Submission Triage. */}
            <div className="flex items-start gap-3">
                <button
                    type="button"
                    aria-label="Back to Submission Triage"
                    onClick={() => navigate("/submissions")}
                    // Aligned near the top of the title row (Andressa); mt-1
                    // nudges the 20px arrow down to sit on the title line.
                    className="text-tertiary hover:text-primary mt-1 shrink-0 cursor-pointer transition duration-100 ease-linear"
                    data-autotest="submission-header-back-button"
                >
                    {/* Design's back-arrow icon (Back Arrow Icon.svg) — stroke
                      * uses currentColor so the tertiary→primary hover applies. */}
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true" className="size-5">
                        <path
                            d="M16.6668 10H3.3335M8.3335 5L3.3335 10L8.3335 15"
                            stroke="currentColor"
                            strokeWidth="1.66667"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
                <div className="flex flex-col gap-0.5 sm:gap-1">
                    {/* Title reduced to text-md / semibold per Figma (Header
                      * Updates) — lighter and smaller than the old display-xs. */}
                    <h1 className="text-md font-semibold text-primary">
                        {idLabel}
                        {titleName && <span className="text-primary"> - {titleName}</span>}
                    </h1>
                    {(subtitleParts.length > 0 || fniBadgeLabel) && (
                        <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                            {subtitleParts.length > 0 && <span className="text-tertiary">{subtitleParts.join(" · ")}</span>}
                            {fniBadgeLabel && (
                                <Badge color="brand" type="pill-color" size="sm" className="max-sm:px-1.5! max-sm:text-[10px]!">
                                    {fniBadgeLabel}
                                </Badge>
                            )}
                        </div>
                    )}
                </div>
            </div>
            {sectionNav}
            {/* Dropdowns row:
              *   < sm: stacked, each full-width
              *   sm – xl: 3 equal columns sharing the row
              *   xl+: flex row with each pinned to w-44 (looks normal next to the title) */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4 xl:flex">
                <Select
                    size="sm"
                    className="w-full xl:w-auto"
                    aria-label="Status"
                    valuePrefix="Status:"
                    data-autotest="submission-header-status-select"
                    selectedKey={detail.status}
                    items={statuses}
                    onSelectionChange={(key) => {
                        if (key === null || key === detail.status) return;
                        onPatch?.({ status: key as SubmissionStatus });
                    }}
                >
                    {(item) => (
                        <Select.Item id={item.id} icon={item.icon}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>
                <Select
                    size="sm"
                    className="w-full xl:w-auto"
                    aria-label="Underwriter"
                    valuePrefix="Underwriter:"
                    data-autotest="submission-header-underwriter-select"
                    placeholder="Select UW"
                    icon={<NeutralUserBadge />}
                    selectedKey={selectedKeyForId(detail.assignee)}
                    items={underwriterItems}
                    onSelectionChange={(key) => {
                        const next = keyToNumber(key);
                        if (next === detail.assignee) return;
                        onPatch?.({ assignee: next });
                    }}
                >
                    {(item) => (
                        <Select.Item id={item.id} icon={item.icon}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>
                <Select
                    size="sm"
                    className="w-full xl:w-auto"
                    aria-label="Approval UW"
                    valuePrefix="Approval UW:"
                    data-autotest="submission-header-approval-select"
                    placeholder="Select UW"
                    icon={<NeutralUserBadge />}
                    selectedKey={selectedKeyForId(detail.approver)}
                    items={approverItems}
                    onSelectionChange={(key) => {
                        const next = keyToNumber(key);
                        if (next === detail.approver) return;
                        onPatch?.({ approver: next });
                    }}
                >
                    {(item) => (
                        <Select.Item id={item.id} icon={item.icon}>
                            {item.label}
                        </Select.Item>
                    )}
                </Select>
            </div>
        </header>
    );
}
